import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Trash2, AlertCircle, Calendar, Target, Clock } from 'lucide-react';

import { Card, CardContent, CardHeader, CardTitle } from '../../ui/Card';
import { Input } from '../../ui/Input';
import { Button } from '../../ui/Button';

const TimelineRisksStep = ({ formData, updateFormData, errors, setErrors }) => {
  const [milestones, setMilestones] = useState(formData.milestones || []);
  const [risksText, setRisksText] = useState(formData.risks_challenges || '');

  // Update parent form data whenever data changes
  useEffect(() => {
    updateFormData({ 
      milestones: milestones,
      risks_challenges: risksText 
    });
  }, [milestones, risksText, updateFormData]);

  const addMilestone = () => {
    if (milestones.length < 10) {
      const newMilestone = {
        title: '',
        date: '',
        description: ''
      };
      setMilestones([...milestones, newMilestone]);
    }
  };

  const removeMilestone = (index) => {
    const newMilestones = milestones.filter((_, i) => i !== index);
    setMilestones(newMilestones);
  };

  const updateMilestone = (index, field, value) => {
    const newMilestones = [...milestones];
    newMilestones[index] = { ...newMilestones[index], [field]: value };
    setMilestones(newMilestones);
  };

  const validateMilestone = (milestone, index) => {
    const newErrors = { ...errors };
    const errorKey = `milestone_${index}`;

    if (!milestone.title) {
      newErrors[errorKey] = 'Milestone title is required';
    } else if (milestone.title.length > 100) {
      newErrors[errorKey] = 'Milestone title is too long';
    } else if (!milestone.date) {
      newErrors[errorKey] = 'Milestone date is required';
    } else if (new Date(milestone.date) <= new Date()) {
      newErrors[errorKey] = 'Milestone date must be in the future';
    } else if (!milestone.description) {
      newErrors[errorKey] = 'Milestone description is required';
    } else if (milestone.description.length > 300) {
      newErrors[errorKey] = 'Milestone description is too long';
    } else {
      delete newErrors[errorKey];
    }

    setErrors(newErrors);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  const getDaysFromNow = (dateString) => {
    if (!dateString) return 0;
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = date - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  return (
    <div className="space-y-6 max-w-full overflow-hidden px-1 sm:px-0">
      {/* Header */}
      <Card className="border-0 shadow-soft">
        <CardHeader className="p-4 sm:p-6">
          <CardTitle className="text-xl sm:text-h4 font-heading text-neutral-900 flex items-center gap-2">
            📅 Timeline & Risks
          </CardTitle>
        </CardHeader>
        <CardContent className="p-4 sm:p-6 pt-0 sm:pt-0">
          <p className="text-sm sm:text-p2-r text-neutral-600">
            Set project milestones and identify potential challenges. This helps donors understand your project timeline and shows you've thought through potential obstacles.
          </p>
        </CardContent>
      </Card>

      {/* Project Milestones */}
      <Card className="border-0 shadow-soft">
        <CardHeader className="p-4 sm:p-6 pb-2 sm:pb-3">
          <CardTitle className="text-base sm:text-p1-m font-heading text-neutral-900 flex items-center gap-2">
            🎯 Project Milestones
            <span className="text-[10px] sm:text-p2-r text-neutral-500 font-normal">(Optional)</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-4 sm:p-6 pt-0 sm:pt-0 space-y-4">
          <p className="text-xs sm:text-p2-r text-neutral-500">
            Break down your project into key milestones with target dates.
          </p>

          {/* Milestones List */}
          <AnimatePresence>
            {milestones.map((milestone, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.98 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.98 }}
                className="mb-4 last:mb-0"
              >
                <Card className="border border-neutral-200 bg-neutral-50/30 hover:shadow-sm transition-all duration-300">
                  <CardHeader className="p-3 sm:p-4 pb-2 border-b border-neutral-100/50">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-sm sm:text-p2-m font-semibold text-neutral-900 flex items-center gap-2">
                        <div className="p-1 px-2 bg-primary-teal/10 text-primary-teal rounded text-[10px] font-bold">
                          {index + 1}
                        </div>
                        Milestone
                      </CardTitle>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeMilestone(index)}
                        className="text-red-500 hover:text-red-700 hover:bg-red-50 h-8 w-8 p-0"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {/* Title and Date */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-p2-m text-neutral-900 mb-2">
                          Milestone Title *
                        </label>
                        <Input
                          type="text"
                          value={milestone.title}
                          onChange={(e) => updateMilestone(index, 'title', e.target.value)}
                          onBlur={() => validateMilestone(milestone, index)}
                          placeholder="e.g., Complete MVP"
                          maxLength={100}
                        />
                      </div>
                      <div>
                        <label className="block text-p2-m text-neutral-900 mb-2">
                          Target Date *
                        </label>
                        <div className="relative">
                          <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-500" />
                          <Input
                            type="date"
                            value={milestone.date}
                            onChange={(e) => updateMilestone(index, 'date', e.target.value)}
                            onBlur={() => validateMilestone(milestone, index)}
                            min={new Date().toISOString().split('T')[0]}
                            className="pl-10"
                          />
                        </div>
                        {milestone.date && (
                          <p className="text-p2-r text-neutral-600 mt-1 flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {getDaysFromNow(milestone.date)} days from now
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Description */}
                    <div>
                      <label className="block text-p2-m text-neutral-900 mb-2">
                        Description *
                      </label>
                      <textarea
                        value={milestone.description}
                        onChange={(e) => updateMilestone(index, 'description', e.target.value)}
                        onBlur={() => validateMilestone(milestone, index)}
                        placeholder="Describe what will be accomplished by this milestone..."
                        rows={3}
                        maxLength={300}
                        className="w-full px-4 py-3 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-teal focus:border-transparent resize-none transition-all duration-200"
                      />
                      <p className="text-p2-r text-neutral-500 mt-1">
                        {milestone.description.length}/300 characters
                      </p>
                    </div>

                    {/* Validation error for this milestone */}
                    {errors[`milestone_${index}`] && (
                      <div className="flex items-center gap-1 text-red-600">
                        <AlertCircle className="w-4 h-4" />
                        <span className="text-p2-r">{errors[`milestone_${index}`]}</span>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </AnimatePresence>

          {/* Add Milestone Button */}
          {milestones.length < 10 && (
            <Card className="border-2 border-dashed border-neutral-300 hover:border-primary-teal transition-all duration-300">
              <CardContent className="p-4 text-center">
                <Button
                  variant="outline"
                  onClick={addMilestone}
                  className="flex items-center gap-2 mx-auto"
                >
                  <Plus className="w-4 h-4" />
                  Add Milestone
                </Button>
                <p className="text-p2-r text-neutral-600 mt-2">
                  Add up to {10 - milestones.length} more {milestones.length === 9 ? 'milestone' : 'milestones'}
                </p>
              </CardContent>
            </Card>
          )}
        </CardContent>
      </Card>

      {/* Risks and Challenges */}
      <Card className="border-0 shadow-soft">
        <CardHeader>
          <CardTitle className="text-p1-m text-neutral-900 flex items-center gap-2">
            ⚠️ Risks & Challenges
            <span className="text-p2-r text-neutral-500 font-normal">(Optional)</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <label className="block text-p2-m text-neutral-900 mb-2">
              What challenges might you face and how will you handle them?
            </label>
            <textarea
              value={risksText}
              onChange={(e) => setRisksText(e.target.value)}
              placeholder="Identify potential risks, challenges, or obstacles you might encounter during the project and explain how you plan to address them. This shows donors you've thought through the project thoroughly."
              rows={6}
              maxLength={2000}
              className="w-full px-4 py-3 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-teal focus:border-transparent resize-none transition-all duration-200"
            />
            <div className="flex justify-between items-center mt-2">
              <p className="text-p2-r text-neutral-500">
                Being transparent about challenges builds trust
              </p>
              <span className={`text-p2-r ${risksText.length > 2000 ? 'text-red-600' : 'text-neutral-500'}`}>
                {risksText.length}/2000
              </span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tips */}
      <Card className="bg-orange-50 border-orange-200 border-2">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <Calendar className="w-5 h-5 text-orange-600 mt-0.5 flex-shrink-0" />
            <div className="text-p2-r text-orange-800">
              <p className="font-semibold mb-1">Timeline Tips</p>
              <ul className="space-y-1 text-sm">
                <li>• Set realistic and achievable milestones</li>
                <li>• Include major project phases or deliverables</li>
                <li>• Be honest about potential challenges</li>
                <li>• Show you have a clear plan and timeline</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default TimelineRisksStep;
